<template>
  <NoData
    v-if="filteredOverlays.length === 0"
    title="No Overlays"
    subtitle="No map overlays found"
  />
  <div class="h-full" v-else>
    <div
      v-for="overlay in filteredOverlays"
      :key="overlay.id"
      class="border-b border-gray-100"
    >
      <VLink
        block
        :to="`/overlays/${overlay.id}/edit`"
        class="bg-white hover:bg-gray-100"
      >
        <div class="flex py-[15px] pl-6 pr-[48px] md:pl-12 md:pr-[56px]">
          <OverlayListItem
            class="w-full"
            :overlay="overlay"
            @delete="handleDelete"
          />
        </div>
      </VLink>
    </div>
  </div>
  <CommonBottomButtonSection>
    <Button
      class="w-full"
      icon="SquaresPlusIcon"
      @click="router.push({ name: 'OverlayCreateView' })"
    >
      New Overlay
    </Button>
  </CommonBottomButtonSection>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useQueryClient } from '@tanstack/vue-query';
import { deleteMapOverlay } from '@gpx-spa/queries';
import { useSearchStore } from '@/stores/searchStore';
import { COMMON_DELETE_SUCCESS, COMMON_DELETE_ERROR } from '@/config/constants';
import VLink from '@/components/ui/VLink.vue';
import { Button } from '@gpx-spa/ui';
import NoData from '@/components/_common/NoData.vue';
import CommonBottomButtonSection from '@/components/_common/CommonBottomButtonSection.vue';
import OverlayListItem from './OverlayListItem.vue';

const props = defineProps({
  overlays: {
    type: Array,
    default: () => [],
  },
  selectedOverlayId: {
    type: Number,
    default: null,
  },
});

defineEmits(['select']);

const store = useStore();
const router = useRouter();
const searchStore = useSearchStore();
const queryClient = useQueryClient();
const accountId = computed(() => store.getters['auth/accountId']);

const { mutateAsync: deleteMutation } = deleteMapOverlay();

const filteredOverlays = computed(() => {
  const query = searchStore.normalizedQuery;
  if (!query) return props.overlays;
  return props.overlays.filter((overlay) =>
    overlay.name?.toLowerCase().includes(query),
  );
});

function handleDelete(overlay) {
  store.dispatch('modal/open', {
    name: 'CommonConfirmationModal',
    params: {
      actionLabel: 'Delete',
      title: overlay.name,
      body: 'Are you sure you want to delete this overlay?',
      onOk: () =>
        deleteMutation({ accountId: accountId.value, id: overlay.id }),
      callback: () =>
        queryClient.invalidateQueries({ queryKey: ['account-map-overlays'] }),
      successMessage: COMMON_DELETE_SUCCESS,
      errorMessage: COMMON_DELETE_ERROR,
    },
  });
}
</script>
